import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { Send, AlertCircle, Smile, Image, Film, Camera } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import { toast } from '../ui/Toaster';
import EmojiPicker from './EmojiPicker';
import GifPicker, { GifData } from './GifPicker';
import UpgradeModal from '../modals/UpgradeModal';
import { MessageService } from '../../services/messageService';
import { SendMessageData } from '../../types/chatTypes';
import { convertLastWordToEmoji } from '../../utils/simpleTextToEmoji';
import { isMobile } from '../../utils/mobileUtils';
import { getRecommendedInputPadding, onViewportChange } from '../../utils/safeAreaUtils';

interface MessageInputProps {
  groupId: string;
  disabled?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({ groupId, disabled = false }) => {
  const [content, setContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitTimeLeft, setRateLimitTimeLeft] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [dynamicPadding, setDynamicPadding] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  // Client-side rate limiting tracking
  const messageTimestamps = useRef<number[]>([]);
  const MAX_MESSAGES = 10; // Same as backend limit
  const TIME_WINDOW = 60000; // 60 seconds in milliseconds

  const { sendMessage } = useSocket();
  const {
    isConnected,
    addOptimisticMessage,
    updateMessageStatus,
    markMessageSent,
    markMessageFailed,
    removeOptimisticMessage
  } = useChatStore();
  const { user, updateUser } = useAuthStore();

  // Check if user has premium chat enhancements
  const hasPremiumChat = user?.chatEnhancements || false;


  // Auto-focus on component mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Dynamic safe area detection for mobile devices
  useEffect(() => {
    if (!isMobile()) return;

    // Calculate initial padding
    const initialPadding = getRecommendedInputPadding();
    setDynamicPadding(initialPadding);

    // Listen for viewport changes (dynamic navigation bars and keyboard)
    const cleanup = onViewportChange(() => {
      const newPadding = getRecommendedInputPadding();
      setDynamicPadding(newPadding);
    });

    return cleanup;
  }, []);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  // Check if user is sending messages too fast (client-side rate limiting)
  const isClientRateLimited = () => {
    const now = Date.now();

    // Remove timestamps older than the time window
    messageTimestamps.current = messageTimestamps.current.filter(
      timestamp => now - timestamp < TIME_WINDOW
    );

    // Check if user has reached the limit
    return messageTimestamps.current.length >= MAX_MESSAGES;
  };

  // Add timestamp when message is sent
  const addMessageTimestamp = () => {
    messageTimestamps.current.push(Date.now());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendMessageContent();
  };

  const handleEmojiSelect = (emoji: string) => {
    const newContent = content + emoji;
    setContent(newContent);
    setShowEmojiPicker(false);

    // Focus back to input and set cursor to end
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newContent.length, newContent.length);
      }
    }, 50);
  };

  const handleGifSelect = (gif: GifData) => {
    // Send GIF message immediately
    sendMessageContent({
      messageType: 'GIF',
      gifUrl: gif.url,
      gifId: gif.id,
    });
    setShowGifPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
    setShowGifPicker(false); // Close GIF picker if open
  };

  // The media button opens a small menu (GIF zoeken / Foto uploaden) for
  // premium users; without premium it opens the beer-donation upgrade flow.
  const toggleAttachMenu = () => {
    if (!hasPremiumChat) {
      setShowEmojiPicker(false);
      setShowGifPicker(false);
      setShowUpgradeModal(true);
      return;
    }
    setShowAttachMenu(!showAttachMenu);
    setShowEmojiPicker(false);
    setShowGifPicker(false);
  };

  // GIF tab inside the emoji picker: straight to the GIF picker
  const handleGifTabClick = () => {
    setShowEmojiPicker(false);
    if (hasPremiumChat) {
      setShowGifPicker(true);
    } else {
      setShowUpgradeModal(true);
    }
  };

  // Photo upload: validate client-side, upload via REST, then send the
  // returned URL as an IMAGE message over the socket
  const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // matches backend MAX_FILE_SIZE
  const handleImageFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // Allow re-selecting the same file later
    if (!file) return;

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Fout', 'Alleen JPG- of PNG-foto\'s zijn toegestaan');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Fout', 'Foto te groot (max 2MB)');
      return;
    }

    try {
      setIsUploadingImage(true);
      const url = await MessageService.uploadChatImage(file);
      await sendMessageContent({ messageType: 'IMAGE', gifUrl: url });
    } catch (error: any) {
      console.error('Failed to upload chat image:', error);
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        toast.error('Fout', 'Foto uploaden mislukt');
      }
    } finally {
      setIsUploadingImage(false);
    }
  };


  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessageContent();
    } else if (e.key === ' ') {
      // Convert text to emoji on spacebar press
      const converted = convertLastWordToEmoji(content);
      if (converted !== content) {
        setContent(converted + ' ');
        e.preventDefault(); // Prevent double space
      }
    } else if (e.key === 'Escape') {
      if (showEmojiPicker || showGifPicker || showAttachMenu) {
        // Close pickers first
        setShowEmojiPicker(false);
        setShowGifPicker(false);
        setShowAttachMenu(false);
      } else if (content.trim()) {
        // If there's content, clear it
        setContent('');
      } else {
        // If no content, blur the input
        inputRef.current?.blur();
      }
    }
  };

  const handleRateLimit = (timeLeft: number, fromServer: boolean = false) => {
    setIsRateLimited(true);
    setRateLimitTimeLeft(timeLeft);

    // Don't show toast for client-side rate limiting, the UI banner is enough
    if (fromServer) {
      // Only show toast for server-side rate limiting (backup case)
      console.warn('Server-side rate limit hit - client-side prevention failed');
    }

    // Clear any existing countdown
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    // Start countdown timer
    countdownRef.current = setInterval(() => {
      setRateLimitTimeLeft(prev => {
        if (prev <= 1) {
          setIsRateLimited(false);
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendMessageContent = async (messageData?: Partial<SendMessageData>) => {
    const trimmed = content.trim();
    // GIF and IMAGE messages carry their content in gifUrl; text is an
    // optional caption for both
    const isMediaMessage = messageData?.messageType === 'GIF' || messageData?.messageType === 'IMAGE';

    // For media messages, content can be empty (no caption required)
    if (!isMediaMessage && (!trimmed || isSending || !isConnected || !user)) {
      return;
    }

    // For text/emoji messages, validate content
    if (!isMediaMessage && trimmed.length > 1000) {
      toast.error('Fout', 'Bericht te lang (max 1000 tekens)');
      return;
    }

    // For media captions, validate length
    if (isMediaMessage && trimmed.length > 500) {
      toast.error('Fout', 'Caption te lang (max 500 tekens)');
      return;
    }

    if (!isConnected || !user) {
      toast.error('Fout', 'Niet verbonden met chat');
      return;
    }

    // Check client-side rate limiting BEFORE doing anything
    if (isClientRateLimited()) {
      handleRateLimit(60);
      return;
    }

    // Track this message attempt for client-side rate limiting
    addMessageTimestamp();

    // Create optimistic message
    const optimisticMessage = {
      id: '',
      content: isMediaMessage ? (trimmed || '') : trimmed,
      messageType: messageData?.messageType || 'TEXT',
      ...(messageData?.gifUrl && { gifUrl: messageData.gifUrl }),
      ...(messageData?.gifId && { gifId: messageData.gifId }),
      type: 'text' as const,
      groupId: groupId,
      userId: user.id.toString(),
      username: user.username,
      userProfilePhoto: user.profilePhoto,
      createdAt: new Date().toISOString(),
    };

    // Add optimistic message immediately
    const tempId = addOptimisticMessage(groupId, optimisticMessage);
    setContent('');

    // Close any open pickers
    setShowEmojiPicker(false);
    setShowGifPicker(false);

    // Maintain focus after sending message
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);

    try {
      setIsSending(true);

      // Send the message with enhanced data
      const sendData: SendMessageData = {
        groupId: parseInt(groupId),
        content: isMediaMessage ? (trimmed || '') : trimmed,
        messageType: messageData?.messageType || 'TEXT',
        ...(messageData?.gifUrl && { gifUrl: messageData.gifUrl }),
        ...(messageData?.gifId && { gifId: messageData.gifId }),
      };

      // The server ack returns the persisted message (real id/createdAt). The
      // sender never receives its own `new-message` broadcast, so this is what
      // flips the optimistic message from 'sending' (clock) to 'sent' (check).
      const realMessage = await sendMessage(sendData);
      if (realMessage) {
        updateMessageStatus(groupId, tempId, realMessage);
      } else {
        // No payload returned but the send succeeded: at least clear the
        // perpetual 'sending' state by marking the optimistic message sent.
        markMessageSent(groupId, tempId);
      }
    } catch (error: any) {
      console.error('Failed to send message:', error);

      if (error.message?.includes('Message rate limit exceeded')) {
        removeOptimisticMessage(groupId, tempId);
        const timeMatch = error.message?.match(/(\d+) seconds?/);
        const timeLeft = timeMatch ? parseInt(timeMatch[1]) : 60;
        handleRateLimit(timeLeft, true);
      } else {
        markMessageFailed(groupId, tempId);
        toast.error('Fout', error.message || 'Kon bericht niet verzenden');
      }
    } finally {
      setIsSending(false);
    }
  };

  const isDisabled = disabled || !isConnected || isSending || isRateLimited;
  const placeholder = !isConnected
    ? 'Niet verbonden met chat...'
    : isRateLimited
    ? `Te snel - wacht ${rateLimitTimeLeft}s...`
    : 'Typ je bericht...';


  return (
    <div
      className="border-t border-white/[.06] p-4 relative"
      style={isMobile() ? { paddingBottom: `${dynamicPadding}px` } : {}}
    >
      {/* Emoji Picker */}
      {showEmojiPicker && (
        <EmojiPicker
          onEmojiSelect={handleEmojiSelect}
          onClose={() => setShowEmojiPicker(false)}
          onGifClick={handleGifTabClick}
        />
      )}

      {/* GIF Picker */}
      {showGifPicker && (
        <GifPicker
          onGifSelect={handleGifSelect}
          onClose={() => setShowGifPicker(false)}
        />
      )}

      {/* Attach menu: GIF zoeken / Foto uploaden */}
      {showAttachMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowAttachMenu(false)} />
          <div className="absolute bottom-full left-4 mb-2 z-50 w-56 rounded-card border border-white/[.08] bg-surface shadow-panel overflow-hidden">
            <button
              type="button"
              onClick={() => { setShowAttachMenu(false); setShowGifPicker(true); }}
              className="flex w-full items-center gap-3 px-4 py-3 text-sm font-medium text-fg-soft hover:bg-white/[.06] transition-colors"
            >
              <Film className="h-5 w-5 text-neon-cyan" />
              GIF zoeken
            </button>
            <button
              type="button"
              onClick={() => { setShowAttachMenu(false); fileInputRef.current?.click(); }}
              className="flex w-full items-center gap-3 px-4 py-3 text-sm font-medium text-fg-soft hover:bg-white/[.06] transition-colors border-t border-white/[.06]"
            >
              <Camera className="h-5 w-5 text-neon-pink" />
              Foto uploaden
            </button>
          </div>
        </>
      )}

      {/* Hidden file input for photo uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="hidden"
        onChange={handleImageFileSelected}
      />

      <form onSubmit={handleSubmit}>
        <div className="flex items-end gap-2 rounded-item border border-neon-pink/30 bg-white/[.05] p-2 pl-3">
          {/* Input controls */}
          <div className="flex items-center gap-1 self-end pb-0.5">
            {/* Emoji button - always visible */}
            <button
              type="button"
              onClick={toggleEmojiPicker}
              className={`
                grid h-9 w-9 place-items-center rounded-[9px] transition-colors
                ${showEmojiPicker
                  ? 'bg-white/[.08] text-neon-pink'
                  : 'text-fg-faint hover:text-neon-cyan hover:bg-white/[.06]'
                }
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              disabled={isDisabled}
              title="Emoji toevoegen"
            >
              <Smile className="h-5 w-5" />
            </button>

            {/* Media button - always visible; premium gets a GIF/photo menu,
                without premium it opens the upgrade flow */}
            <button
              type="button"
              onClick={toggleAttachMenu}
              className={`
                grid h-9 w-9 place-items-center rounded-[9px] transition-colors
                ${showGifPicker || showAttachMenu
                  ? 'bg-white/[.08] text-neon-pink'
                  : 'text-fg-faint hover:text-neon-cyan hover:bg-white/[.06]'
                }
                ${isDisabled || isUploadingImage ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              disabled={isDisabled || isUploadingImage}
              title={hasPremiumChat ? 'GIF of foto sturen' : 'GIFs en foto\'s vrijspelen (steun ons met een biertje)'}
            >
              {isUploadingImage ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Image className="h-5 w-5" />
              )}
            </button>
          </div>

          <div className="flex-1 relative min-w-0">
            <textarea
              ref={inputRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              disabled={isDisabled}
              rows={1}
              className={`
                block w-full border-0 bg-transparent px-1 py-2 text-[15px] text-fg
                placeholder-fg-faint placeholder:text-sm
                focus:outline-none focus:ring-0
                resize-none overflow-hidden
                ${isDisabled ? 'cursor-not-allowed opacity-60' : ''}
              `}
              style={{
                minHeight: '40px',
                maxHeight: '120px',
              }}
              onInput={(e) => {
                // Auto-resize textarea
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
              }}
            />

            {/* Character counter */}
            {content.length > 800 && (
              <div className={`
                absolute bottom-1 right-2 font-mono text-[11px]
                ${content.length > 1000 ? 'text-neon-pink-soft' : 'text-fg-faint'}
              `}>
                {content.length}/1000
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isDisabled || !content.trim()}
            className={`
              grid h-[42px] w-[42px] min-w-[42px] flex-shrink-0 place-items-center rounded-[11px] transition-all
              ${
                isDisabled || !content.trim()
                  ? 'bg-white/[.06] text-fg-faint cursor-not-allowed'
                  : 'bg-neon-gradient text-ink shadow-neon-pink-strong hover:opacity-90'
              }
            `}
            title={isSending ? 'Verzenden...' : isRateLimited ? `Wacht ${rateLimitTimeLeft}s` : 'Bericht verzenden'}
          >
            {isSending ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : isRateLimited ? (
              <span className="font-mono text-xs font-semibold">{rateLimitTimeLeft}s</span>
            ) : (
              <Send className="h-[19px] w-[19px]" />
            )}
          </button>
        </div>
      </form>

      {/* Upgrade flow for GIF access (chat enhancements) */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        upgradeType="CHAT_ENHANCEMENTS"
        onUpgradeComplete={() => updateUser({ chatEnhancements: true })}
      />

      {/* Connection warning */}
      {!isConnected && (
        <div className="mt-2 rounded-field border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink-soft">
          Niet verbonden met chatserver. Berichten kunnen niet worden verzonden.
        </div>
      )}

      {/* Rate limit warning - prominent spam warning */}
      {isRateLimited && (
        <div className="mt-2 rounded-field border border-neon-pink/40 bg-neon-pink/10 px-4 py-3 text-sm text-neon-pink-soft">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-neon-pink" />
              <span className="font-medium">
                Je verstuurt berichten te snel! Doe het wat rustiger aan en probeer het later opnieuw.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[11px] uppercase tracking-[.16em] text-fg-meta">Wacht</span>
              <span className="font-bold text-lg text-neon-pink">{rateLimitTimeLeft}s</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageInput;